import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from 'next-themes';
import { useContent } from './contexts/ContentContext';
import './App.css';

// PrimeReact components - Complete migration
import { Galleria } from 'primereact/galleria';
import { Button as PrimeButton } from 'primereact/button';
import { Dialog as PrimeDialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Card as PrimeCard } from 'primereact/card';
import { Panel } from 'primereact/panel';
import { TabView, TabPanel } from 'primereact/tabview';
import { RadioButton } from 'primereact/radiobutton';
import { Checkbox } from 'primereact/checkbox';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { FileUpload } from 'primereact/fileupload';
import { Divider } from 'primereact/divider';
import { Ripple } from 'primereact/ripple';

// Enable Ripple effect globally
import PrimeReact from 'primereact/api';
PrimeReact.ripple = true;

// Import shadcn UI components (still needed during migration)
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { Progress } from './components/ui/progress';
import { Separator } from './components/ui/separator';
import { Alert, AlertDescription } from './components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './components/ui/dialog';
import { toast, Toaster } from 'sonner';

// Make toast globally available for error handlers
window.toast = toast;

import SortCategoriesModal from './components/SortCategoriesModal';
import ProductAttributesModalV2 from './components/ProductAttributesModalV2';
import OrderSummaryModal from './components/OrderSummaryModal';
import CheckoutSection from './components/CheckoutSection';
import AddressModal from './components/AddressModal';
import ContentEditModal from './components/ContentEditModal';
import UserManagement from './components/admin/UserManagement';
import AdminOrdersSection from './components/admin/AdminOrdersSection';
import ProductManagement from './components/admin/ProductManagement';
import ShippingManagement from './components/admin/ShippingManagement';
import CategoryManagement from './components/admin/CategoryManagement';
import CouponManagement from './components/admin/CouponManagement';
import ReportsSection from './components/admin/ReportsSection';
import SettingsSection from './components/admin/SettingsSection';
import RecycleBinSection from './components/admin/RecycleBinSection';
import TaxRateManagementSection from './components/admin/TaxRateManagementSection';
import ProfileSection from './components/ProfileSection';
import OrdersSection from './components/OrdersSection';
import CroppedImage from './components/CroppedImage';
import PaymentSection from './components/PaymentSection';
import StepNavigation from './components/StepNavigation';
import ImageCustomizer from './components/ImageCustomizer';
import UploadSection from './components/UploadSection';
import SelectSection from './components/SelectSection';
import CartSection from './components/CartSection';
import ImageEditorModal from './components/ImageEditorModal';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.baseURL = API;

// Add axios interceptor to attach token to every request
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Duplicate components were removed - using the full implementations above

// Main App Component
function App() {
  return (
    <AuthProvider>
      <Toaster 
        position="top-center" 
        richColors 
        duration={3000}
        style={{ zIndex: 99999 }}
        toastOptions={{
          style: {
            fontSize: '16px',
            padding: '16px 20px',
            minWidth: '400px',
            maxWidth: '600px',
            zIndex: 99999
          },
          className: 'toast-large'
        }}
      />
      <Router>
        <div className="App">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}




export default App;